import {
	VelocityEnv,
	OracleInfo,
	Wallet,
	initialize,
} from '@velocity-exchange/sdk';
import { CandleType, TradeOffsetPrice, UIEnv } from '@velocity-exchange/common';
import { Adapter } from '@solana/wallet-adapter-base';
import { PriorityFee, SlippageTolerance } from '../@types/types';
import { Commitment, Version } from '@solana/web3.js';

export enum VelocityTheme {
	light = 'light-theme',
	dark = 'dark-theme',
}

export type ToastsToIgnoreSettings = {
	walletVersionedTxCompatibility: boolean;
};

export type SubscriberType = 'polling' | 'websocket';
export type EventSubscriberType = SubscriberType | 'events-server';
export type SelfEventSubscriberType = EventSubscriberType;
export type GlobalEventSubscriberType =
	| EventSubscriberType
	| 'dlob-server-trade-publisher'
	| 'data-api-trade-publisher';
export type CandleTypeByMarketIdKey = Record<string, CandleType>;

export type UserSettings = {
	rpcEndpoint: {
		label: string;
		value: string;
	};
	devnetOverride: boolean;
	stagingDlobServerOverride: boolean;
	stagingDataServerOverride: boolean;
	enableVersionedTx: boolean;
	showPnlOnClose: boolean;
	showTradeConfirmation: boolean;
	showClosePositionConfirmation: boolean;
	slippageTolerance: SlippageTolerance;
	showAccountValues: boolean;
	showFillNotifications: boolean;
	showZeroValues: boolean;
	blinkOnValueChange: boolean;
	showPositionsAndOrdersOnChart: boolean;
	showLiquidationPriceOnChart: boolean;
	showMarketBar: boolean;
	showElfaWidget: boolean;
	showBuysAndSellsOnChart: boolean;
	perpPageTableSizePreference: string;
	priorityFee: 'custom' | 'dynamic' | 'boosted' | 'turbo';
	customPriorityFee: number | undefined;
	maxPriorityFeeCap: number;
	candleTypeByMarketIdKey: CandleTypeByMarketIdKey;
	charts?: any[];
	explorer: 'solana' | 'solscan' | 'xray' | 'solanafm';
	/**
	 * The auction ramp a trader chose, in wall-clock seconds. Converted to slots
	 * at the live slot length wherever an order is built.
	 */
	auctionDurationSeconds: number;
	auctionStartPriceOffsetFrom: TradeOffsetPrice | 'marketBased';
	auctionStartPriceOffset: number | 'marketBased';
	auctionEndPriceOffsetFrom: TradeOffsetPrice;
	auctionEndPriceOffset: number;
	oracleOffsetOrdersEnabled: boolean;
	customMarginRatio: number;
	accountSubscriberType: SubscriberType; // This controls the type of account subscriber used by the Drift Client / RPC. See NetworkSettings.tsx for more details of the different types.
	cancelExistingOrdersOnClosePosition: boolean;
	ignoreToasts: ToastsToIgnoreSettings;
	placeAndTakeEnabled: boolean;
	directRouteSwaps: boolean;
	version?: number;
	traderProfile?: 'default' | 'custom';
	theme: 'dark' | 'light' | 'device' | null;
	globalEventsSubscriptionType: GlobalEventSubscriberType;
	selfEventsSubscriptionType: EventSubscriberType;
	/** This will be `undefined` until the user explicitly disables / enables Swift. */
	swiftEnabled: boolean | undefined;
	showOrderbook: boolean;
	showOrderbookOnMobileTradeView: boolean;
	swapClientType: 'titan' | 'jupiter';
	enableSignlessTrading: boolean;
};

export type EnvironmentVariables = {
	sdkEnv: VelocityEnv;
	sdkConfig: ReturnType<typeof initialize>;
	defaultRpcAddress: string;
	defaultRpcLabel: string;
	dataApiUrl: string;
	dataApiWsUrl: string;
	dataApiEnvToUse: UIEnv;
	devProxyEnvSegment: 'staging' | 'mainnet' | 'dev';
	dlobServerHttpUrl: string;
	dlobWsServerToUse: string;
	eventsServerToUse?: string;
	pollingFrequencyMs: number;
	selectedMarketPollingCadenceMs?: number;
	activeMarketPollingCadenceMs?: number;
	backgroundMarketPollingCadenceMs?: number;
	oracleInfos: OracleInfo[];
	maxAccountsPerWallet: number;
	locale: string;
	defaultWallet: Wallet;
	E2E_WALLET: Adapter;
	priceDivergenceWarningThreshold: number;
	minInitialDepositValue: number;
	defaultMakerRebateBps: number;
	defaultTakerFeeBps: number;
	dynamicPriorityFeeMultiplier?: number;
	uiEssentialUpdateIntervalMs?: number;
	uiNonEssentialUpdateIntervalMs?: number;
	dynamicFeeTargetPercentile?: number;
	feeSubscriptionPollingMultiplier?: number;
	maxAccountsSwapBuffer: number;
	velocityDialectDappAddress: string;
	feePayerPublicKey: string;
	dialectNotificationsEnabled: boolean;
	useSimulatedComputeUnitBudget: boolean;
	useSimulatedComputeUnitBudgetForFees: boolean;
	computeUnitBufferMultiplier: number;
	minimumPriorityFee: PriorityFee;
	txSenderRetryInterval?: number;
	initialDataSourceMaxDelay: number;
	newDataSourceSubMaxDelay: number;
	upgradeDataSourceDelay: number;
	placeAndTakeLowerBoundCu?: number;
	useCachedBlockhashFetcherInVelocityClient: boolean;
	showAppKitWallet: boolean;
	rpcVersion?: Version;
	defaultAuctionDurationPercentage: number;
	perpsMaxLeverage: number;
	swiftServerUrl: string;
	disablePervasiveOracleCrank: boolean;
	/**
	 * Wall-clock budget for the user to approve a signed-message order in their
	 * wallet, before the auction starts. Converted to slots at the live slot
	 * length at the call site.
	 */
	signedMessageOrderSigningBudgetMs: number;
	/**
	 * The same budget for a signer that needs no human approval, such as a
	 * delegated embedded wallet. Shorter, because the stamped slot is the auction
	 * start and the auction cannot begin until it arrives.
	 */
	delegateSignedMessageOrderSigningBudgetMs: number;
	marketBasedAuctionStartPriceOffsetMajor: number;
	marketBasedAuctionStartPriceOffsetNonMajor: number;
	enableLstPool: boolean;
	/**
	 * Show the Private Beta perks card on the trade page (2 bps fees +
	 * accelerated referral bonus). Sourced from
	 * NEXT_PUBLIC_ENABLE_PRIVATE_BETA_PERKS.
	 */
	enablePrivateBetaPerks: boolean;
	/**
	 * Open Beta mode. Swaps the Private Beta labels for their Open Beta wording
	 * and force-disables the wallet whitelist gate regardless of the
	 * ENABLE_PRIVATE_BETA_WALLET_WHITELIST feature flag. Sourced from
	 * NEXT_PUBLIC_ENABLE_OPEN_BETA.
	 */
	enableOpenBeta: boolean;
	oracleCrankStalenessThresholdMs: number;
	delistingConfig: { marketIndices: number[]; timestamp: number }[];
	nextDelistingTs: number;
	/**
	 * Perp market indexes to hide from the market selector dropdown, sourced from
	 * NEXT_PUBLIC_HIDE_MARKET_INDEXES (comma-separated, e.g. "1,27,66").
	 */
	hideMarketIndexes: number[];
	/**
	 * Number of slots past auction end before we consider it expired (fallback guard).
	 */
	auctionExpirySlotBuffer: number;
	/**
	 * Additional milliseconds added to order.maxTs when checking expiration.
	 */
	orderMaxTsBufferMs: number;
	defaultCommitmentLevel: Commitment;
	/**
	 * API key for Fun.xyz UDA (Universal Deposit Address) integration.
	 */
	funXyzApiKey?: string;
};
